import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  Input,
  SimpleChanges,
  OnChanges,
  EventEmitter,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormControl,
  ValidatorFn,
  Validators,
} from '@angular/forms';

type ErrorState = 'warning' | 'error' | null;

@Component({
  selector: 'lib-email-validator',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './email-validator.component.html',
  styleUrls: ['./email-validator.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class EmailValidatorComponent implements OnChanges {
  /** Optional: fieldLabel text */
  @Input() fieldLabel = 'E-Mail';
  @Input() placeholder = 'xxxx@xx.xxxx';
  @Input() isSubmitted = false;
  @Input() isRequired = true;
  /** Optional: control width/layout externally */
  @Input() fullWidth = true;
  @Output() inputFieldData: EventEmitter<string | null> = new EventEmitter();
  @Output() emailChange: EventEmitter<string | null> = new EventEmitter();
  public errorState: ErrorState = null;
  public errorMessageText = '';

  emailControl = new FormControl<string>('', {
    validators: this.getEmailValidators(),
    nonNullable: true,
  });

  ngOnChanges(changes: SimpleChanges) {
    if (changes['isRequired']) {
      this.emailControl.setValidators(this.getEmailValidators());
      this.emailControl.updateValueAndValidity({ emitEvent: false });
    }

    if (changes['isSubmitted'] || changes['isRequired']) {
      this.updateErrorState();
    }
  }

  /** Trigger validation and callback on blur */
  onBlur(): void {
    this.updateErrorState();
    const val = this.emailControl.value;
    if (val && this.emailControl.valid) {
      this.inputFieldData.emit(val);
      this.emailChange.emit(val);
    } else {
      this.inputFieldData.emit(null);
      this.emailChange.emit(null);
    }
  }

  /** Update error message and state */
  private updateErrorState(): void {
    if (this.emailControl.hasError('required')) {
      this.errorMessageText = 'Email is required.';
      this.errorState = this.isSubmitted ? 'error' : 'warning';
    } else if (
      this.emailControl.hasError('email') ||
      this.emailControl.hasError('pattern')
    ) {
      this.errorMessageText =
        'Please enter a valid email address (e.g., test@test.com).';
      this.errorState = this.isSubmitted ? 'error' : 'warning';
    } else {
      this.resetError();
    }
  }

  resetError() {
    this.errorMessageText = '';
    this.errorState = null;
  }

  private getEmailValidators(): ValidatorFn[] {
    const validators: ValidatorFn[] = [
      Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/),
    ];

    if (this.isRequired) {
      validators.unshift(Validators.required);
    }

    return validators;
  }
}
